// src/components/Events/AllEvents.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import './AllEvents.css';

const AllEvents = () => {
    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'upcoming', 'past'
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchAllEvents = async () => {
            try {
                setIsLoading(true);
                
                const response = await api.get('/events/upcoming');
                
                const processed = response.data.map(event => ({
                    ...event,
                    banner: {
                        background: buildImageUrl(event.banner?.background) || `${UPLOADS_URL}/uploads/default/banner.png`,
                        logo: buildImageUrl(event.banner?.logo) || `${UPLOADS_URL}/uploads/default/logo.png`,
                    }
                }));
                
                setEvents(processed);
                setFilteredEvents(processed);
                
            } catch (err) {
                console.error('Error al cargar todos los eventos:', err);
                setError('No se pudieron cargar los eventos en este momento.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchAllEvents();
    }, []);

    // Lógica combinada de Búsqueda y Filtros Cronológicos
    useEffect(() => {
        let result = events;
    
        if (searchTerm.trim() !== '') {
            result = result.filter(event => 
                (event.title && event.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (event.slug && event.slug.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
    
        const today = new Date();
        if (activeFilter === 'upcoming') {
            result = result.filter(event => {
                const eventDate = event.eventDate || event.end_date || event.startDate;
                return eventDate && new Date(eventDate) >= today;
            });
        } else if (activeFilter === 'past') {
            result = result.filter(event => {
                const eventDate = event.eventDate || event.end_date || event.startDate;
                return eventDate && new Date(eventDate) < today;
            });
        }
    
        setFilteredEvents(result);
    }, [searchTerm, activeFilter, events]);

    const buildImageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        return path.startsWith('/') ? `${UPLOADS_URL}${path}` : `${UPLOADS_URL}/${path}`;
    };

    if (isLoading) return <div className="events-loading">🔍 Buscando competencias...</div>;
    if (error) return <div className="events-error-msg">⚠️ {error}</div>;

    return (
        <div className="all-events-page-container">
            {/* Cabecera Creativa del Componente */}
            <div className="events-page-hero">
                <h1>Calendario de Competencias</h1>
                <p>Encuentra tu próximo desafío, inscríbete y mide tus tiempos con precisión.</p>
            </div>

            {/* Panel de Control de Usuario: Buscador y Filtros con Enfoque Visual */}
            <div className="events-control-panel">
                <div className="search-bar-wrapper">
                    <span className="search-icon">🔍</span>
                    <input 
                        type="text" 
                        placeholder="Buscar evento por nombre..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input-field"
                    />
                </div>

                <div className="filter-buttons-group">
                    <button 
                        className={`filter-tab-btn ${activeFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('all')}
                    >
                        <span className="btn-icon">🏃‍♂️</span> Todos
                    </button>
                    <button 
                        className={`filter-tab-btn ${activeFilter === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('upcoming')}
                    >
                        <span className="btn-icon">📅</span> Próximos
                    </button>
                    <button 
                        className={`filter-tab-btn ${activeFilter === 'past' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('past')}
                    >
                        <span className="btn-icon">🏁</span> Finalizados
                    </button>
                </div>
            </div>

            {/* Contador de resultados */}
            <div className="results-counter">
                🏼 Se encontraron <strong>{filteredEvents.length}</strong> eventos disponibles
            </div>

            {/* Grilla de Tarjetas de Eventos */}
            {filteredEvents.length === 0 ? (
                <div className="no-events-found">
                    <span className="no-events-icon">👁️‍🗨️</span>
                    <h3>No encontramos eventos que coincidan</h3>
                    <p>Prueba cambiando los filtros o ajustando tu búsqueda.</p>
                </div>
            ) : (
                <div className="all-events-grid-layout">
                    {filteredEvents.map(event => (
                        <GridEventCard key={event.slug} event={event} />
                    ))}
                </div>
            )}
        </div>
    );
};

/* Componente Interno Optimizado para la Vista de Rejilla Completa */
const GridEventCard = ({ event }) => {
    const dateValue = event.eventDate || event.end_date || event.startDate || event.createdAt;
    
    const getFormattedDateParts = (dateString) => {
        if (!dateString) return { day: '--', month: '---' };
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return { day: '--', month: '---' };
            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleString('es-ES', { month: 'short' }).toUpperCase().replace('.', '');
            return { day, month };
        } catch (e) {
            return { day: '--', month: '---' };
        }
    };
    const getLocationText = () => {
        if (event.location_name) {
            return event.location_name;
        }
        if (event.location_city) {
            return event.location_city;
        }
        return "Ubicación por confirmar";
    };

    const { day, month } = getFormattedDateParts(dateValue);

    return (
        <Link to={`/${event.slug}`} className="grid-card-anchor-wrapper">
            <div className="grid-event-card-box">
                <div className="grid-card-media-header">
                    <img 
                        src={event.banner?.background} 
                        alt={event.slug || event.title}
                        className="grid-card-bg-img"
                    />
                    <div className="grid-card-dimmer-overlay"></div>
                    
                    {/* Badge Cronológico Superior */}
                    <div className="grid-card-date-badge">
                        <span className="grid-badge-day">{day}</span>
                        <span className="grid-badge-month">{month}</span>
                    </div>

                    {/* Chip de Estado Dinámico para facilidad visual */}
                    <span className={`status-pill ${new Date(dateValue) >= new Date() ? 'status-open' : 'status-closed'}`}>
                        {new Date(dateValue) >= new Date() ? '🟢 Abierto' : '🔴 Concluido'}
                    </span>
                </div>

                <div className="grid-card-content-body">
                    <h3 className="grid-card-title-text">
                        {event.slug || 'Evento Deportivo'}
                    </h3>
                    
                    {/* Fila Informativa de Iconos de Conveniencia */}
                    <div className="grid-card-icon-info-row">
                        <div className="info-icon-item">
                            <span className="geo-icon">📍</span>
                            <span className="info-text-value">{getLocationText()}</span>
                        </div>
                        <div className="info-icon-item">
                            <span className="geo-icon">⏱️</span>
                            <span className="info-text-value">Sistema de Chips RFID</span>
                        </div>
                    </div>
                    
                    {/* Footer de la tarjeta con acción implícita */}
                    <div className="grid-card-action-footer">
                        <span className="action-hint-text">Ver Convocatoria y Registro</span>
                        <span className="action-arrow-icon">➡️</span>
                    </div>
                </div>
            </div>
        </Link>
    );
};

export default AllEvents;